import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../../hooks/UseAuth";
import { useError } from "../../components/common/ErrorHandler";
import { useTheme } from "../../context/ThemeContext";
import { useBranding } from "../../context/BrandingContext";
import { useTokens } from "../../hooks/useTokens";
import JwtUtils from "../../utils/jwtUtils";
import Logger from "../../utils/logger";
import AuroraBackground from "../../components/AuroraBackground";
import BrandLogoLockup from "../../components/BrandLogoLockup";
import { HiEye, HiEyeOff } from "react-icons/hi";

/* ══════════════════════════════════════════════
   INJECTION KEYFRAMES (une seule fois)
══════════════════════════════════════════════ */
if (typeof document !== 'undefined' && !document.getElementById('_alp_kf')) {
  const s = document.createElement('style');
  s.id = '_alp_kf';
  s.textContent = `
    @keyframes _alp_fadeUp   { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:translateY(0)} }
    @keyframes _alp_spin     { to{transform:rotate(360deg)} }
    @keyframes _alp_shake    { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-5px)} 40%,80%{transform:translateX(5px)} }
    @keyframes _alp_orbA     { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(40px,-30px) scale(1.08)} }
    @keyframes _alp_orbB     { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-30px,40px) scale(1.06)} }
    @keyframes _alp_orbC     { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(20px,20px) scale(1.04)} }

    ._alp_input:focus {
      border-color: var(--alp-blue-focus) !important;
      background: var(--alp-blue-focus-bg) !important;
      box-shadow: 0 0 0 3px var(--alp-blue-focus-ring) !important;
    }
    ._alp_input::placeholder { color: var(--alp-placeholder); }

    ._alp_submit:not(:disabled):hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 32px var(--alp-btn-shadow) !important;
    }
    ._alp_submit:not(:disabled):active { transform: translateY(0); }

    ._alp_link:hover { color: var(--alp-t1) !important; text-decoration: underline; }
    ._alp_eye:hover  { color: var(--alp-t1) !important; }

    ._alp_input:-webkit-autofill,
    ._alp_input:-webkit-autofill:hover,
    ._alp_input:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0 1000px var(--alp-input-bg) inset !important;
      box-shadow: 0 0 0 1000px var(--alp-input-bg) inset !important;
      -webkit-text-fill-color: var(--alp-t1) !important;
      caret-color: var(--alp-t1) !important;
      background-color: var(--alp-input-bg) !important;
      border-color: var(--alp-border) !important;
      filter: none !important;
      transition: background-color 999999s ease-in-out 0s, color 999999s ease-in-out 0s !important;
    }
    ._alp_input::selection {
      background: rgba(53,144,227,0.32);
      color: var(--alp-t1);
    }
  `;
  document.head.appendChild(s);
}

/* ══════════════════════════════════════════════
   ATOMES UI
══════════════════════════════════════════════ */

/** Étiquette de champ */
const FieldLabel = ({ htmlFor, children, T }) => (
  <label htmlFor={htmlFor} style={{
    display: 'block', marginBottom: 7,
    fontSize: '10.5px', fontWeight: 700,
    color: T.text3, letterSpacing: '0.12em', textTransform: 'uppercase',
  }}>
    {children}
  </label>
);

/** Input standard */
const NInput = React.forwardRef(({ id, type = 'text', value, onChange, placeholder, autoComplete, required, rightSlot, T }, ref) => (
  <div style={{ position: 'relative' }}>
    <input
      ref={ref} id={id} type={type} value={value} onChange={onChange}
      placeholder={placeholder} autoComplete={autoComplete} required={required}
      className="_alp_input"
      style={{
        width: '100%', boxSizing: 'border-box',
        padding: rightSlot ? '11px 44px 11px 14px' : '11px 14px',
        borderRadius: 10, fontSize: '13.5px',
        fontFamily: "'Space Grotesk', sans-serif",
        background: T.inputBg,
        border: `1px solid ${T.blueBorder}`,
        color: T.text1, outline: 'none',
        transition: 'border-color 0.15s, background 0.15s, box-shadow 0.15s',
      }}
    />
    {rightSlot && (
      <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}>
        {rightSlot}
      </div>
    )}
  </div>
));

/** Bouton principal */
const SubmitBtn = ({ isLoading, children, T }) => (
  <button type="submit" disabled={isLoading} className="_alp_submit"
    style={{
      width: '100%', padding: '13px', border: 'none', borderRadius: 12,
      background: `linear-gradient(135deg, ${T.btnColor}, ${T.headerColor})`,
      color: '#fff', fontSize: '13px', fontWeight: 800,
      letterSpacing: '0.08em', cursor: isLoading ? 'not-allowed' : 'pointer',
      opacity: isLoading ? 0.65 : 1,
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
      boxShadow: `0 4px 16px ${T.btnBorder}`,
      transition: 'transform 0.15s, box-shadow 0.15s, opacity 0.15s',
      fontFamily: "'Space Grotesk', sans-serif",
    }}
  >
    {children}
  </button>
);

const Spin = () => (
  <div style={{
    width: 16, height: 16, borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.25)', borderTopColor: '#fff',
    animation: '_alp_spin 0.8s linear infinite', flexShrink: 0,
  }}/>
);

/** Bannière d'erreur */
const ErrorBanner = ({ message, T }) => (
  <div style={{
    display: 'flex', alignItems: 'flex-start', gap: 10,
    padding: '12px 14px', borderRadius: 10,
    background: `${T.red}14`,
    border: `1px solid ${T.red}40`,
    animation: '_alp_shake 0.4s ease',
  }}>
    <span style={{ fontSize: 15, flexShrink: 0, lineHeight: 1.4 }}>⚠️</span>
    <p style={{ margin: 0, fontSize: '12.5px', color: T.red, lineHeight: 1.5 }}>{message}</p>
  </div>
);

/* ══════════════════════════════════════════════
   LOGO NSIA - branding dynamique (logoAuth en priorité)
══════════════════════════════════════════════ */
const LogoBadge = ({ logoUrl, height = 44, marginBottom = 16, T }) => (
  <div style={{ display: 'flex', justifyContent: 'center', margin: `0 0 ${marginBottom}px` }}>
    <BrandLogoLockup logoUrl={logoUrl} alt="NSIA Assurance Vie" logoHeight={height} center T={T} />
  </div>
);

/* ══════════════════════════════════════════════
   PAGE PRINCIPALE
══════════════════════════════════════════════ */
const AdminLoginPage = () => {
  const [username, setUsername]         = useState("");
  const [password, setPassword]         = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]               = useState("");
  const [isLoggingIn, setIsLoggingIn]   = useState(false);

  const { login, user, loading, error: authError } = useAuth();
  const { showWarning } = useError();
  const { theme, toggleTheme } = useTheme();
  const { branding } = useBranding();
  const T = useTokens();
  const isLight = theme === "light";
  const navigate = useNavigate();
  const location = useLocation();

  /* ── CSS variables dynamiques injectées dans le DOM ── */
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--alp-blue-focus',     `${T.blue}99`);
    root.style.setProperty('--alp-blue-focus-bg',  `${T.blue}12`);
    root.style.setProperty('--alp-blue-focus-ring',`${T.blue}1F`);
    root.style.setProperty('--alp-placeholder',    T.text3);
    root.style.setProperty('--alp-btn-shadow',     `${T.btnColor}80`);
    root.style.setProperty('--alp-t1',             T.text1);
    root.style.setProperty('--alp-input-bg',       T.inputBg);
    root.style.setProperty('--alp-border',         T.blueBorder);
  }, [T]);

  /* ── Logique métier 100% inchangée ── */
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get("expired") === "true") {
      showWarning("Your session has expired. Please log in again.");
      navigate("/admin/login", { replace: true });
    }
    if (location.state?.message) {
      showWarning(location.state.message);
      if (location.state?.username) setUsername(location.state.username);
      navigate("/admin/login", { replace: true, state: null });
    }
    if (location.state?.error) {
      setError(location.state.error);
      navigate("/admin/login", { replace: true, state: null });
    }
  }, [location, navigate, showWarning]);

  useEffect(() => {
    if (user && !loading) {
      const from = location.state?.from?.pathname || "/admin/dashboard";
      Logger.debug("User authenticated, redirecting to:", from);
      navigate(from, { replace: true });
    }
  }, [user, loading, location.state, navigate]);

  useEffect(() => {
    const token = JwtUtils.getStoredToken();
    if (token && JwtUtils.isTokenExpired(token)) {
      JwtUtils.removeToken();
      setError("Your previous session expired. Please log in again.");
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!username || !password) {
      setError("Le nom d'utilisateur et le mot de passe sont requis");
      return;
    }
    setIsLoggingIn(true);
    try {
      const success = await login(username, password);
      if (success) navigate("/admin/dashboard");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const displayError = error || authError;

  /* ── Accent bar couleur branding ── */
  const accentBar = `linear-gradient(90deg, ${T.btnColor}, ${T.headerColor}, ${T.teal}, ${T.headerColor}, ${T.btnColor})`;

  return (
    <div style={{
      position: 'relative', minHeight: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: T.bg, fontFamily: "'Space Grotesk', sans-serif",
      overflow: 'hidden',
    }}>
      {/* Fond animé */}
      <AuroraBackground/>

      {/* Grid pattern décoratif */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
        backgroundImage: `linear-gradient(${T.blueBorder} 1px, transparent 1px), linear-gradient(90deg, ${T.blueBorder} 1px, transparent 1px)`,
        backgroundSize: '52px 52px',
        maskImage: 'radial-gradient(ellipse 75% 75% at 50% 50%, black 20%, transparent 100%)',
        WebkitMaskImage: 'radial-gradient(ellipse 75% 75% at 50% 50%, black 20%, transparent 100%)',
      }}/>

      {/* Halos orbitaux animés */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        <div style={{ position: 'absolute', top: '-20%', left: '-15%', width: '62vw', height: '62vw', borderRadius: '50%', background: `radial-gradient(circle, ${T.blue}1F 0%, transparent 65%)`, filter: 'blur(55px)', animation: '_alp_orbA 14s ease-in-out infinite' }}/>
        <div style={{ position: 'absolute', bottom: '-15%', right: '-12%', width: '52vw', height: '52vw', borderRadius: '50%', background: `radial-gradient(circle, ${T.teal}17 0%, transparent 65%)`, filter: 'blur(55px)', animation: '_alp_orbB 18s ease-in-out infinite' }}/>
        <div style={{ position: 'absolute', top: '35%', right: '15%', width: '32vw', height: '32vw', borderRadius: '50%', background: `radial-gradient(circle, ${T.btnColor}0F 0%, transparent 65%)`, filter: 'blur(40px)', animation: '_alp_orbC 22s ease-in-out infinite' }}/>
      </div>

      {/* Card login */}
      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 420, margin: '0 24px' }}>
      <div style={{
        background: isLight ? 'rgba(255,255,255,0.90)' : 'rgba(12,16,40,0.80)',
        border: `1px solid ${T.blueBorder}`,
        borderRadius: 22, overflow: 'hidden',
        backdropFilter: 'blur(28px)',
        WebkitBackdropFilter: 'blur(28px)',
        boxShadow: isLight
          ? `0 1px 0 rgba(255,255,255,0.9) inset, 0 28px 80px ${T.blue}33, 0 0 0 1px ${T.blueBorder}`
          : `0 1px 0 rgba(255,255,255,0.04) inset, 0 28px 90px rgba(0,0,0,0.70), 0 0 0 1px ${T.blueBorder}`,
        animation: '_alp_fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both',
      }}>

        {/* Accent bar dégradé branding */}
        <div style={{ height: 3, background: accentBar, backgroundSize: '300% 100%', animation: '_alp_orbA 8s linear infinite' }}/>

        {/* Reflet interne */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 200, background: `linear-gradient(180deg,${T.blue}0D 0%,transparent 100%)`, pointerEvents: 'none' }}/>

        {/* Cercles décoratifs coin bas-droit */}
        <div style={{ position: 'absolute', bottom: -24, right: -24, width: 150, height: 150, borderRadius: '50%', border: `1px solid ${T.blueBorder}`, pointerEvents: 'none' }}/>
        <div style={{ position: 'absolute', bottom: -48, right: -48, width: 220, height: 220, borderRadius: '50%', border: `1px solid ${T.divider}`, pointerEvents: 'none' }}/>

        <div style={{ padding: '36px 32px 32px', position: 'relative' }}>

          {/* ── Toggle thème ── */}
          <button
            onClick={toggleTheme}
            aria-label="Basculer le thème"
            title={isLight ? 'Passer en mode sombre' : 'Passer en mode clair'}
            style={{
              position: 'absolute', top: 16, right: 16, zIndex: 10,
              width: 34, height: 34, borderRadius: 9,
              border: `1px solid ${T.blueBorder}`,
              background: T.blueSoft,
              color: T.text3, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s, color 0.15s, border-color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = T.blueHover; e.currentTarget.style.color = T.text1; }}
            onMouseLeave={e => { e.currentTarget.style.background = T.blueSoft; e.currentTarget.style.color = T.text3; }}
          >
            {isLight
              ? <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
              : <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
            }
          </button>

          {/* ── Header ── */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <LogoBadge logoUrl={branding.logoAuth || branding.logoUrl} height={44} marginBottom={16} T={T} />
            <p style={{ margin: 0, fontSize: '12.5px', color: T.text3, letterSpacing: '0.04em' }}>
              Connectez-vous à votre espace
            </p>
          </div>

          {/* ── Erreur ── */}
          {displayError && (
            <div style={{ marginBottom: 20 }}>
              <ErrorBanner message={displayError} T={T}/>
            </div>
          )}

          {/* ── Formulaire ── */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

            {/* Nom d'utilisateur */}
            <div>
              <FieldLabel htmlFor="username" T={T}>Nom d'utilisateur</FieldLabel>
              <NInput
                id="username" type="text" value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="Ex : JohnDoe"
                autoComplete="username" required T={T}
              />
            </div>

            {/* Mot de passe */}
            <div>
              <FieldLabel T={T}>Mot de passe</FieldLabel>
              <NInput
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password" required T={T}
                rightSlot={
                  <button type="button" className="_alp_eye"
                    onClick={() => setShowPassword(p => !p)}
                    aria-label={showPassword ? "Masquer" : "Afficher"}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.text3, padding: 0, display: 'flex', alignItems: 'center', transition: 'color 0.15s' }}
                  >
                    {showPassword ? <HiEyeOff size={18}/> : <HiEye size={18}/>}
                  </button>
                }
              />
              <div style={{ marginTop: 8, textAlign: 'right' }}>
                <Link to="/admin/forgot-password" className="_alp_link"
                  style={{ fontSize: '11.5px', color: T.text3, textDecoration: 'none', transition: 'color 0.15s' }}>
                  Mot de passe oublié ?
                </Link>
              </div>
            </div>

            {/* Séparateur */}
            <div style={{ height: 1, background: T.divider, margin: '2px 0' }}/>

            {/* Bouton submit */}
            <SubmitBtn isLoading={isLoggingIn} T={T}>
              {isLoggingIn
                ? <><Spin/>CONNEXION EN COURS...</>
                : <>SE CONNECTER <span style={{ fontSize: 16, lineHeight: 1 }}>-&gt;</span></>
              }
            </SubmitBtn>
          </form>

          {/* ── Footer ── */}
          <p style={{ margin: '22px 0 0', textAlign: 'center', fontSize: '12.5px', color: T.text3 }}>
            Besoin d'aide ? Contactez votre administrateur.
          </p>

        </div>
      </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
